/**
 * Tenant theme system utilities.
 *
 * ThemeProvider uses these to inject CSS custom properties at runtime.
 *
 * CSS variable format: this project uses Tailwind v4 with `@theme inline` in
 * index.css, which maps `--color-primary: var(--primary)` etc. Tailwind
 * utilities like `bg-primary` resolve to `var(--primary)` verbatim — the value
 * is NOT wrapped in hsl(). Hex values (#rrggbb) are valid CSS colors and work
 * correctly. The hsl(var(...)) entries in tailwind.config.js are overridden by
 * @theme inline.
 *
 * Zero-regression guarantee: injectCSSVariables() is only called when the
 * `portal_management` feature flag is enabled for the tenant. Banks
 * Architecture (no flag) never hits this path; index.css defaults stay intact.
 */

export interface TenantTheme {
  colors: {
    primary: string;
    secondary?: string;
    accent?: string;
    background?: string;
    foreground?: string;
    muted?: string;
    destructive?: string;
  };
  typography: { font_family: string };
  branding: {
    logo_url?: string | null;
    favicon_url?: string | null;
    company_name?: string | null;
  };
  dark_mode: { enabled: boolean; auto_detect: boolean };
}

export const DEFAULT_THEME: TenantTheme = {
  colors: {
    primary: '#2563eb',
    secondary: '#7c3aed',
    accent: '#06b6d4',
    background: '#ffffff',
    foreground: '#1f2937',
    muted: '#6b7280',
    destructive: '#dc2626',
  },
  typography: { font_family: 'system-ui' },
  branding: { logo_url: null, favicon_url: null, company_name: null },
  dark_mode: { enabled: false, auto_detect: false },
};

/**
 * Convert a 6-digit hex color to an HSL triplet string (e.g. "217 98% 51%").
 * Returns the bare triplet without the hsl() wrapper, suitable for use as an
 * argument to hsl() when needed. Note: injectCSSVariables() uses hex directly
 * (valid for Tailwind v4 @theme inline), so this function is a utility for
 * callers that specifically need HSL triplet format.
 */
export function hexToHSL(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Compute a WCAG-compliant foreground color (dark gray or white) for the
 * given hex background using relative luminance.
 */
export function contrastForeground(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const L = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return L > 0.179 ? '#1f2937' : '#ffffff';
}

const FONT_URLS: Record<string, string> = {
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
};

function loadFontLink(fontFamily: string): void {
  if (!FONT_URLS[fontFamily]) return;
  if (document.getElementById(`font-${fontFamily}`)) return;
  const link = document.createElement('link');
  link.id = `font-${fontFamily}`;
  link.rel = 'stylesheet';
  link.href = FONT_URLS[fontFamily];
  document.head.appendChild(link);
}

/**
 * Inject tenant theme as CSS custom properties on document.documentElement.
 * Only called by ThemeProvider when portal_management flag is active.
 *
 * This project uses Tailwind v4 with @theme inline in index.css.
 * @theme inline means utilities like bg-primary resolve to var(--color-primary)
 * which maps directly to var(--primary) — the CSS var value is used verbatim
 * as the color, NOT wrapped in hsl(). Therefore values must be valid CSS colors
 * (hex, hsl(...), rgb(...), oklch(...)) — NOT bare HSL triplets like "217 98% 51%".
 *
 * We inject hex values because that is what the theme schema stores. Hex values
 * are valid CSS colors in all contexts.
 */
export function injectCSSVariables(theme: TenantTheme): void {
  const root = document.documentElement;
  const c = { ...DEFAULT_THEME.colors, ...theme.colors };

  // Primary + auto-computed foreground
  root.style.setProperty('--primary', c.primary);
  root.style.setProperty('--primary-foreground', contrastForeground(c.primary));

  // Secondary + foreground
  const secondary = c.secondary ?? DEFAULT_THEME.colors.secondary!;
  root.style.setProperty('--secondary', secondary);
  root.style.setProperty('--secondary-foreground', contrastForeground(secondary));

  // Accent + foreground
  const accent = c.accent ?? DEFAULT_THEME.colors.accent!;
  root.style.setProperty('--accent', accent);
  root.style.setProperty('--accent-foreground', contrastForeground(accent));

  // Background / foreground / muted / destructive
  const bg = c.background ?? DEFAULT_THEME.colors.background!;
  const fg = c.foreground ?? DEFAULT_THEME.colors.foreground!;
  const muted = c.muted ?? DEFAULT_THEME.colors.muted!;
  const destructive = c.destructive ?? DEFAULT_THEME.colors.destructive!;
  root.style.setProperty('--background', bg);
  root.style.setProperty('--foreground', fg);
  root.style.setProperty('--muted', muted);
  root.style.setProperty('--muted-foreground', muted);
  root.style.setProperty('--destructive', destructive);
  root.style.setProperty('--destructive-foreground', '#ffffff');

  // Ring inherits primary
  root.style.setProperty('--ring', c.primary);

  // Font family
  const font = theme.typography?.font_family ?? 'system-ui';
  loadFontLink(font);
  root.style.setProperty('--font-sans', font);

  // Dark mode
  if (theme.dark_mode?.enabled) {
    const prefersDark =
      theme.dark_mode.auto_detect &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark || !theme.dark_mode.auto_detect);
  } else {
    root.classList.remove('dark');
  }

  // Favicon
  if (theme.branding?.favicon_url) {
    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const link = existing ?? document.createElement('link');
    link.rel = 'icon';
    link.href = `${theme.branding.favicon_url}?v=${Date.now()}`;
    if (!existing) document.head.appendChild(link);
  }
}
